import React from "react";
import { SEGMENT_COLORS, getSegmentDisplayName } from "../constants/segments";
import { formatCompactCurrency, formatCurrency, formatNumber } from "../utils/formatters";

const SEGMENT_EXPLANATIONS = {
  Champions: "Highest-value customers with strong recency, frequency, and spend.",
  "Loyal Customers": "Very recent customers with healthy repeat behavior.",
  Promising: "Very recent customers with at least moderate frequency or spend.",
  "Need Attention": "Mid-recency customers who can move up or decline based on engagement.",
  "At Risk": "Previously valuable customers showing churn risk and requiring proactive retention.",
  Lost: "Low-activity or lapsed customers with weak recency and engagement.",
};

function formatWholeNumber(value) {
  return formatNumber(Math.round(Number(value || 0)));
}

export function renderHighlightedInsight(text) {
  const numericPattern = /(₹?Rs\.?\s?[0-9][0-9.,]*\s?(?:Cr|L|k)?|₹[0-9][0-9.,]*(?:Cr|L|k)?|[0-9]+(?:\.[0-9]+)?%|[0-9]+(?:\.[0-9]+)?)/gi;
  const exactNumericPattern = /^(₹?Rs\.?\s?[0-9][0-9.,]*\s?(?:Cr|L|k)?|₹[0-9][0-9.,]*(?:Cr|L|k)?|[0-9]+(?:\.[0-9]+)?%|[0-9]+(?:\.[0-9]+)?)$/i;
  const parts = String(text || "").split(numericPattern);
  return parts.map((part, index) => {
    const isNumeric = exactNumericPattern.test(part.trim());
    return isNumeric ? (
      <span className="segment-insight-num" key={`${part}-${index}`}>
        {part}
      </span>
    ) : (
      <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
    );
  });
}

export function SegmentCrest({ color, segmentName }) {
  const segmentKey = String(segmentName || "").toLowerCase();

  const pathsBySegment = {
    champions: [
      "M8 5.5h8v5.2a4 4 0 0 1-8 0V5.5Z",
      "M8 7.2H5.8v1.6a2.8 2.8 0 0 0 2.8 2.8",
      "M16 7.2h2.2v1.6a2.8 2.8 0 0 1-2.8 2.8",
      "M12 14.7v2.1",
      "M8.7 18.5h6.6",
    ],
    "loyal customers": [
      "M12 18.8s-6-3.6-6-8a3.6 3.6 0 0 1 6-2.7 3.6 3.6 0 0 1 6 2.7c0 4.4-6 8-6 8Z",
      "M9.2 11.8 11.2 13.8 15 9.9",
    ],
    promising: [
      "M6.2 16.8c4.6-.4 8-3.2 10.4-8.4",
      "M12.8 8.2h4.2v4.2",
      "M7.2 8.8h2.7m-2.7 3h5.2",
    ],
    "need attention": [
      "M12 5.5a6.3 6.3 0 0 1 4.5 10.7l-.2.2V18l-1.8 1.8H9.5L7.7 18v-1.6l-.2-.2A6.3 6.3 0 0 1 12 5.5Zm-1 11h2m-3.3 2.8h4.6",
    ],
    "at risk": [
      "M12 5.2 18.2 17H5.8L12 5.2Zm0 4.3v3.8m0 2.2h.01",
    ],
    lost: [
      "M7 7.5h10v9H7v-9Z",
      "M9.1 10.2h5.8",
      "M9.1 13.4h3.2",
      "M6 18.5 18 5.5",
    ],
  };

  const paths = pathsBySegment[segmentKey] || pathsBySegment.promising;

  return (
    <span className="segment-crest" style={{ borderColor: `${color}66` }} aria-hidden="true">
      <svg viewBox="0 0 24 24" role="presentation">
        {paths.map((path) => (
          <path
            key={path}
            d={path}
            fill="none"
            stroke={color}
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}
      </svg>
    </span>
  );
}

export default function SegmentSummary({
  insights = [],
  segments = [],
  customers = [],
  terminology = {},
  onTakeAction,
}) {
  const revenueLabel = terminology.revenue_label || "Revenue";
  const transactionLabel = terminology.transaction_label || "Trip";
  const activityLabel = terminology.activity_label || "Trip";
  const customerPlural = terminology.customer_plural_label || "Customers";

  const metricDefinitions = {
    clients: {
      label: terminology.metric_clients_label || `Total ${customerPlural}`,
      valueKey: "clientCount",
      formatter: formatNumber,
    },
    revenue: {
      label: terminology.metric_revenue_label || `Total ${revenueLabel}`,
      valueKey: "revenue",
      formatter: formatCompactCurrency,
    },
    avgTripValue: {
      label: terminology.metric_value_label || `Avg ${transactionLabel} Value`,
      valueKey: "avgTripValue",
      formatter: formatCurrency,
    },
    avgTripFrequency: {
      label: terminology.metric_frequency_label || `Avg ${activityLabel} Freq`,
      valueKey: "avgTripFrequency",
      formatter: formatWholeNumber,
    },
  };
  const formatPct = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? `${numeric.toFixed(1)}%` : null;
  };

  const getSegmentMetrics = (segmentItem) => {
    const segmentCustomers = customers.filter((customer) => customer.segment === segmentItem.segment);
    const clientCount = Number(segmentItem.customer_count ?? segmentCustomers.length ?? 0);
    const revenue = Number(segmentItem.revenue || 0);
    const visits = segmentCustomers.reduce(
      (sum, customer) => sum + Number(customer.visit_count || 0),
      0
    );

    return {
      clientCount,
      revenue,
      avgTripValue: visits > 0 ? revenue / visits : 0,
      avgTripFrequency: clientCount > 0 ? visits / clientCount : 0,
    };
  };

  const fallbackInsightForSegment = (segmentItem) => {
    const customerShare = formatPct(segmentItem?.customer_share_pct);
    const revenueShare = formatPct(segmentItem?.revenue_share_pct);
    const customerCount = formatNumber(segmentItem?.customer_count || 0);

    if (segmentItem?.segment === "At Risk") {
      return `At Risk customers are ${customerShare || "a notable share"} of your base and contribute ${revenueShare || "significant"} revenue - prioritize retention and win-back actions.`;
    }

    if (segmentItem?.segment === "Lost") {
      return `Lost segment has ${customerCount} customers - run stronger reactivation campaigns to recover dormant revenue opportunities.`;
    }

    return segmentItem?.segment_description || "Customer segment";
  };

  const getInsightForSegment = (segmentName) => {
    const normalizedSegment = String(segmentName || "").toLowerCase();
    return insights.find((insight) =>
      String(insight || "").toLowerCase().includes(normalizedSegment)
    );
  };

  return (
      <section className="card segment-overview-card">
        <div className="segment-overview-head">
          <div className="card-label">Segment Overview</div>
        </div>
        <div className="segment-grid">
          {segments.map((segmentItem) => {
            const color = SEGMENT_COLORS[segmentItem.segment] || "#8a92a3";
            const segmentDisplayName = getSegmentDisplayName(segmentItem.segment);
            const metrics = getSegmentMetrics(segmentItem);
            const segmentInsight =
              getInsightForSegment(segmentItem.segment) ||
              fallbackInsightForSegment(segmentItem);
            const explanation =
              segmentItem.segment_description ||
              SEGMENT_EXPLANATIONS[segmentItem.segment] ||
              "Customer segment";

            return (
              <div
                key={segmentItem.segment}
                className="segment-tile"
                style={{
                  background: `${color}12`,
                  borderColor: `${color}4d`,
                  "--segment-color": color,
                }}
              >
	                <div className="segment-name" style={{ color }}>
	                  <span className="segment-name-main">
	                    <span>{segmentDisplayName}</span>
	                    <strong>{formatNumber(metrics.clientCount || 0)}</strong>
	                  </span>
	                  <SegmentCrest color={color} segmentName={segmentItem.segment} />
	                </div>
	                <div className="segment-mini-metrics segment-mini-metrics-single">
	                  <div className="segment-primary-metric">
	                    <span>{metricDefinitions.clients.label}</span>
	                    <p>{explanation}</p>
	                  </div>
	                </div>
                {segmentInsight ? (
                  <div className="segment-insight">{renderHighlightedInsight(segmentInsight)}</div>
                ) : null}
                <div className="segment-tile-actions">
                  <button
                    type="button"
                    className="segment-action-btn"
                    onClick={() => onTakeAction?.(segmentItem)}
                  >
                    Suggestive Action
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </section>
  );
}
